import { SafetyCertificateOutlined } from '@ant-design/icons';
import { Button, Modal, Space, message } from 'antd';
import { useEffect, useRef, useState } from 'react';
import {
  socialAccountLoginCancel,
  socialAccountLoginFinish,
  socialAccountLoginQrcode,
  socialAccountLoginSms,
  socialAccountLoginStart,
  socialAccountLoginStatus,
} from '../api';
import { resolveErrorMessage } from '../utils/errorMessage';

const SUPPORTED = new Set(['douyin', 'kuaishou', 'weixin_channels']);

const normalizePlatformCode = (account) => String(account?.platform_code || '').trim();

const SocialAccountLoginButton = ({
  account,
  size = 'small',
  type = 'link',
  label = '扫码登录',
  disabled = false,
  autoStart = false,
  onSuccess,
  buttonProps = {},
}) => {
  const platformCode = normalizePlatformCode(account);
  const accountId = account?.account_id;
  const accountName = account?.account_name || accountId;

  const [starting, setStarting] = useState(false);
  const [open, setOpen] = useState(false);
  const [qrImg, setQrImg] = useState('');
  const [statusText, setStatusText] = useState('');
  const [finishing, setFinishing] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [smsRequired, setSmsRequired] = useState(false);
  const [smsImg, setSmsImg] = useState('');
  const [smsPhoneMask, setSmsPhoneMask] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [smsSubmitting, setSmsSubmitting] = useState(false);

  const pollRef = useRef(null);
  const autoStartedRef = useRef(false);

  const cleanupPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const closeModal = async () => {
    cleanupPolling();
    setOpen(false);
    setQrImg('');
    setStatusText('');
    setFinishing(false);
    setSmsRequired(false);
    setSmsImg('');
    setSmsPhoneMask('');
    setSmsCode('');
    if (sessionId) {
      try {
        await socialAccountLoginCancel({ session_id: sessionId });
      } catch (_) {
        // ignore
      }
    }
    setSessionId('');
  };

  useEffect(() => () => cleanupPolling(), []);

  useEffect(() => {
    if (!autoStart) return;
    if (autoStartedRef.current) return;
    autoStartedRef.current = true;
    setTimeout(() => {
      try {
        handleStart();
      } catch (_) {
        // ignore
      }
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  if (!SUPPORTED.has(platformCode)) return null;

  const refreshQr = async (sidOverride) => {
    const isEventLike = sidOverride && typeof sidOverride === 'object' && 'nativeEvent' in sidOverride;
    const sid = (!isEventLike && typeof sidOverride === 'string' && sidOverride) ? sidOverride : sessionId;
    if (!sid) return;
    try {
      setStatusText('正在刷新二维码...');
      const resp = await socialAccountLoginQrcode({ session_id: sid });
      if (resp?.status === 'success' && resp?.qrcode_img) {
        setQrImg(String(resp.qrcode_img));
        setStatusText('请使用手机扫码登录...');
      } else {
        setQrImg('');
        message.error(resolveErrorMessage(resp?.error_message, '刷新二维码失败'));
      }
    } catch (e) {
      message.error(resolveErrorMessage(e?.message || String(e), '刷新二维码失败'));
    }
  };

  const finishIfLoggedIn = async (sidOverride) => {
    const sid = sidOverride || sessionId;
    if (!sid) return;
    setFinishing(true);
    try {
      setStatusText('登录成功，正在保存 cookies 并同步到 OSS/ES...');
      const resp = await socialAccountLoginFinish({ session_id: sid });
      if (resp?.status === 'success') {
        message.success('登录成功，cookies 已写回配置');
        await closeModal();
        if (typeof onSuccess === 'function') onSuccess(resp);
      } else {
        message.error(resolveErrorMessage(resp?.error_message, '保存 cookies 失败'));
      }
    } catch (e) {
      message.error(resolveErrorMessage(e?.message || String(e), '保存 cookies 失败'));
    } finally {
      setFinishing(false);
    }
  };

  const startPolling = (sid) => {
    cleanupPolling();
    if (!sid) return;
    pollRef.current = setInterval(async () => {
      try {
        const s = await socialAccountLoginStatus({ session_id: sid });
        if (s?.status !== 'success') return;
        const st = String(s?.login_status || '').trim();
        if (st === 'logged_in') {
          cleanupPolling();
          await finishIfLoggedIn(sid);
          return;
        }
        if (st === 'sms_required') {
          cleanupPolling();
          setSmsRequired(true);
          setQrImg('');
          setSmsImg(String(s?.sms_img || ''));
          setSmsPhoneMask(String(s?.sms_phone_mask || ''));
          setStatusText('需要短信验证码，请在下方输入后继续');
          return;
        }
        if (st === 'blocked') {
          cleanupPolling();
          setStatusText(`登录被平台拦截（${s?.blocked_reason || '风控/验证码'}），请稍后重试或人工处理`);
          return;
        }
        if (st === 'failed') {
          cleanupPolling();
          const reason = resolveErrorMessage(s?.error, '未知原因');
          setStatusText(`检测失败：${reason}`);
          return;
        }
        if (st === 'pending_confirm') {
          setStatusText(String(s?.login_hint || '已扫码，请在手机上确认登录...'));
          return;
        }
        const hint = String(s?.login_hint || '').trim();
        setStatusText(hint || '请使用手机扫码登录...');
      } catch (_) {
        // ignore transient
      }
    }, 2500);
  };

  const submitSms = async () => {
    if (!sessionId) return;
    const code = String(smsCode || '').trim();
    if (!code) {
      message.warning('请输入短信验证码');
      return;
    }
    setSmsSubmitting(true);
    try {
      const resp = await socialAccountLoginSms({ session_id: sessionId, sms_code: code });
      if (resp?.status !== 'success') {
        message.error(resolveErrorMessage(resp?.error_message, '验证码提交失败'));
        return;
      }
      setSmsRequired(false);
      setSmsImg('');
      setSmsPhoneMask('');
      setSmsCode('');
      setStatusText('验证码已提交，正在验证登录状态...');
      startPolling(sessionId);
    } catch (e) {
      message.error(resolveErrorMessage(e?.message || String(e), '验证码提交失败'));
    } finally {
      setSmsSubmitting(false);
    }
  };

  const handleStart = async () => {
    if (!accountId) {
      message.error('缺少 account_id');
      return;
    }
    setStarting(true);
    try {
      const resp = await socialAccountLoginStart({ account_id: accountId });
      if (resp?.status !== 'success') {
        message.error(resolveErrorMessage(resp?.error_message, '启动扫码登录失败'));
        return;
      }
      const sid = String(resp?.session_id || '');
      if (!sid) {
        message.error('启动扫码登录失败：缺少 session_id');
        return;
      }
      setSessionId(sid);
      const img = String(resp?.qrcode_img || '');
      if (img) setQrImg(img);
      setOpen(true);
      setStatusText('请使用手机扫码登录...');
      startPolling(sid);
      if (!img) {
        setTimeout(() => {
          refreshQr(sid);
        }, 1200);
      }
    } catch (e) {
      message.error(resolveErrorMessage(e?.message || String(e), '启动扫码登录失败'));
    } finally {
      setStarting(false);
    }
  };

  return (
    <>
      <Button
        type={type}
        size={size}
        icon={<SafetyCertificateOutlined />}
        onClick={handleStart}
        loading={starting}
        disabled={disabled || starting || finishing}
        {...buttonProps}
      >
        {label}
      </Button>

      <Modal
        title={`扫码登录：${accountName || '账号'}（${platformCode}）`}
        open={open}
        onCancel={closeModal}
        footer={(
          <Space>
            <Button onClick={closeModal}>
              关闭
            </Button>
          </Space>
        )}
      >
        {smsRequired ? (
          <div style={{
            minHeight: 220,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
          >
            <div style={{ color: '#333', marginBottom: 8 }}>
              {statusText || '需要短信验证码，请在下方输入后继续'}
            </div>
            {smsPhoneMask && (
              <div style={{ color: '#666', marginBottom: 12 }}>验证码手机号：{smsPhoneMask}</div>
            )}
            <div style={{ display: 'flex', gap: 8, width: '100%', maxWidth: 360 }}>
              <input
                value={smsCode}
                onChange={(e) => setSmsCode(e.target.value)}
                placeholder="请输入短信验证码"
                style={{
                  flex: 1,
                  height: 32,
                  padding: '0 8px',
                  borderRadius: 6,
                  border: '1px solid #d9d9d9',
                }}
              />
              <Button type="primary" onClick={submitSms} loading={smsSubmitting}>
                提交
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <div style={{
              width: 260,
              height: 260,
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#fff',
            }}
            >
              {qrImg ? (
                <img src={qrImg} alt="social_login_qrcode" style={{ width: 240, height: 240 }} />
              ) : (
                <div style={{ color: '#999' }}>二维码加载中...</div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#333', marginBottom: 8 }}>扫码后会自动检测登录状态，并同步 cookies。</div>
              <div style={{ color: '#999' }}>{statusText || '-'}</div>
              {finishing && <div style={{ color: '#999', marginTop: 8 }}>正在上传 OSS 并回写配置...</div>}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default SocialAccountLoginButton;
